import React, { FC, useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import Dialog from '@mui/material/Dialog';
import Slider from '@mui/material/Slider';
import CircularProgress from '@mui/material/CircularProgress';
import { createUseStyles } from 'react-jss';
import { useTheme } from '@mui/material/styles';
import color from 'color';
import CropIcon from '@mui/icons-material/Crop';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';

import { Button } from '@shared/components/Button';
import { ThemeProps } from '@styles/theme';
import CloseIcon from '@assets/close-icon.svg';

const ASPECT_RATIO = 16 / 10;
const ASPECT_LABEL = '16 : 10';

function formatAspectLabel(ratio: number): string {
    if (ratio >= 1) {return `${ratio.toFixed(2)} : 1`;}

    return `1 : ${(1 / ratio).toFixed(2)}`;
}

// ── Canvas crop utility ──────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', reject);
        img.setAttribute('crossOrigin', 'anonymous');
        img.src = src;
    });
}

async function getCroppedBlob(imageSrc: string, pixelCrop: Area, mimeType = 'image/jpeg'): Promise<Blob> {
    const image = await loadImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {throw new Error('Could not get canvas context');}

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            blob => {
                if (blob) {resolve(blob);} else {reject(new Error('Canvas is empty'));}
            },
            mimeType,
            mimeType === 'image/jpeg' ? 0.92 : undefined,
        );
    });
}

// ── Styles ───────────────────────────────────────────────────────────────────

const useStyles = createUseStyles((theme: ThemeProps) => ({
    paper: {
        borderRadius: '12px !important',
        overflow: 'hidden !important',
        maxWidth: '680px !important',
        width: 'min(680px, calc(100vw - 32px)) !important',
        background: `${theme.colors.grey_background} !important`,
        boxShadow: '0 24px 64px rgba(0,0,0,0.38) !important',
    },

    header: {
        flexShrink: 0,
        background: theme.isDark
            ? 'linear-gradient(135deg, #0d0a1e 0%, #1a1040 55%, #0a1a2e 100%)'
            : `linear-gradient(135deg, ${theme.colors.violet_darkest} 0%, ${theme.colors.violet_primary} 55%, ${theme.colors.deep_blue} 100%)`,
        padding: '18px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50px',
            right: '-30px',
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color(theme.colors.blue_neon).alpha(0.2).string()} 0%, transparent 70%)`,
            filter: 'blur(20px)',
            pointerEvents: 'none',
        },
    },

    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1,
        position: 'relative',
    },

    headerTitle: {
        fontFamily: theme.font.secondary,
        fontSize: 16,
        fontWeight: 600,
        color: '#ffffff',
        letterSpacing: '0.01em',
    },

    headerBadge: {
        padding: '2px 8px',
        borderRadius: 3,
        background: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.28)',
        fontSize: 11,
        fontFamily: theme.font.secondary,
        letterSpacing: '0.06em',
        color: 'rgba(255,255,255,0.88)',
    },

    closeBtn: {
        zIndex: 1,
        position: 'relative',
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.10)',
        backdropFilter: 'blur(8px)',
        border: '1.5px solid rgba(255,255,255,0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 0.2s ease, border-color 0.2s ease',
        '&:hover': {
            background: 'rgba(255,255,255,0.22)',
            borderColor: 'rgba(255,255,255,0.38)',
        },
        '& img': {
            width: 13,
            height: 13,
            filter: 'brightness(0) invert(1)',
        },
    },

    cropperWrapper: {
        position: 'relative',
        width: '100%',
        height: 380,
        background: '#0e0b20',
        overflow: 'hidden',
    },

    hint: {
        textAlign: 'center',
        fontSize: 11,
        color: theme.colors.grey_text,
        opacity: 0.6,
        letterSpacing: '0.03em',
        marginTop: 6,
        fontFamily: theme.font.primary,
    },

    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 24px 0',
    },

    zoomIcon: {
        fontSize: '20px !important',
        color: `${color(theme.colors.violet_primary).alpha(0.7).string()} !important`,
        flexShrink: 0,
    },

    slider: {
        flex: 1,
        color: `${theme.colors.violet_primary} !important`,
        '& .MuiSlider-thumb': {
            width: 14,
            height: 14,
            boxShadow: `0 0 0 3px ${color(theme.colors.violet_primary).alpha(0.18).string()} !important`,
        },
        '& .MuiSlider-track': {
            height: 3,
        },
        '& .MuiSlider-rail': {
            height: 3,
            opacity: 0.28,
        },
    },

    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 10,
        padding: '14px 24px 20px',
    },
}));

// ── Component ────────────────────────────────────────────────────────────────

export type ImageCropModalProps = {
    isOpen: boolean;
    imageSrc: string;
    mimeType?: string;
    minAspectRatio?: number;
    maxAspectRatio?: number;
    onClose: () => void;
    onCropComplete: (blob: Blob, previewUrl: string) => void;
};

export const ImageCropModal: FC<ImageCropModalProps> = ({
    isOpen,
    imageSrc,
    mimeType = 'image/jpeg',
    minAspectRatio,
    maxAspectRatio,
    onClose,
    onCropComplete,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const isAspectDynamic = minAspectRatio !== undefined && maxAspectRatio !== undefined;
    const [aspectRatio, setAspectRatio] = useState<number>(
        minAspectRatio !== undefined && maxAspectRatio !== undefined
            ? (minAspectRatio + maxAspectRatio) / 2
            : ASPECT_RATIO
    );

    const handleCropComplete = useCallback((_: Area, pixels: Area) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleConfirm = async () => {
        if (!croppedAreaPixels) {return;}
        setIsProcessing(true);
        try {
            const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, mimeType);
            const previewUrl = URL.createObjectURL(blob);
            onCropComplete(blob, previewUrl);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (!isProcessing) {onClose();}
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            PaperProps={{ className: styles.paper }}
            disableEscapeKeyDown={isProcessing}
        >
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <CropIcon style={{ color: 'rgba(255,255,255,0.88)', fontSize: 20 }} />
                    <span className={styles.headerTitle}>Crop Image</span>
                    <span className={styles.headerBadge}>{isAspectDynamic ? formatAspectLabel(aspectRatio) : ASPECT_LABEL}</span>
                </div>
                <div className={styles.closeBtn} onClick={handleClose}>
                    <img src={CloseIcon} alt="close" />
                </div>
            </div>

            {/* Cropper */}
            <div className={styles.cropperWrapper}>
                <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspectRatio}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={handleCropComplete}
                    showGrid
                    style={{
                        containerStyle: { position: 'absolute', inset: 0 },
                        mediaStyle: {},
                        cropAreaStyle: {
                            border: '2px solid rgba(255,255,255,0.9)',
                            boxShadow: '0 0 0 9999px rgba(10, 7, 25, 0.68)',
                            borderRadius: 4,
                        },
                    }}
                />
            </div>

            <p className={styles.hint}>Drag to reposition · Scroll or use the slider to zoom</p>

            {/* Zoom slider */}
            <div className={styles.controlsRow}>
                <ZoomOutIcon className={styles.zoomIcon} />
                <Slider
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.01}
                    onChange={(_, v) => setZoom(v as number)}
                    className={styles.slider}
                    size="small"
                />
                <ZoomInIcon className={styles.zoomIcon} />
            </div>

            {/* Aspect ratio slider */}
            {isAspectDynamic && (
                <div className={styles.controlsRow}>
                    <CropPortraitIcon className={styles.zoomIcon} />
                    <Slider
                        value={aspectRatio}
                        min={minAspectRatio}
                        max={maxAspectRatio}
                        step={0.01}
                        onChange={(_, v) => setAspectRatio(v as number)}
                        className={styles.slider}
                        size="small"
                    />
                    <CropLandscapeIcon className={styles.zoomIcon} />
                </div>
            )}

            {/* Footer */}
            <div className={styles.footer}>
                <Button onClick={handleClose} color="secondary" disabled={isProcessing}>
                    Cancel
                </Button>
                <Button onClick={handleConfirm} disabled={isProcessing}>
                    {isProcessing
                        ? <><CircularProgress size={14} style={{ marginRight: 6, color: 'inherit' }} />Processing…</>
                        : 'Crop & Upload'
                    }
                </Button>
            </div>
        </Dialog>
    );
};
